/**
 * TaskRow — flat single row.
 * Renders only the columns that are currently visible (passed as `columns` prop).
 *
 * Column order: Owner · Event · Task · Status · Priority · Closure · Time · Remarks · Comments
 *
 * Task cell:
 *   - minWidth controlled by taskMinWidth prop (user-resizable via Task col handle)
 *   - Height auto-grows with content — never clipped
 *   - white-space: normal; word-break: break-word  (wraps at words, not characters)
 *   - Rich-text: double-click to edit, Enter to commit, Esc to cancel
 *   - SECURITY: titles are user-controlled HTML. They pass through
 *     sanitizeHtml() both before rendering (dangerouslySetInnerHTML) and
 *     before saving, so stored markup can never carry scripts/attributes.
 *
 * All other cells: white-space: nowrap so they auto-size to their content.
 * Overdue: red left inset border + faint red bg.
 *
 * Memoised: list re-renders (e.g. one row's realtime update) skip rows
 * whose task object hasn't changed.
 */

import { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import StatusBadge from './Statusbadge.jsx';
import { secondsToMinutes, formatMinutes } from '../../utils/timeUtils.js';
import { PRIORITY_LABELS } from '../../data/constants.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { getTaskRoute } from '../../utils/roleUtils.js';
import { sanitizeHtml, htmlToText } from '../../utils/sanitizeHtml.js';
import { supabase } from '../../api/supabase.js';
import Icon from '../ui/Icons.jsx';

/* ── helpers ──────────────────────────────────────────────── */
function fmtDate(str) {
  if (!str) return '—';
  const d = typeof str === 'string' ? parseISO(str) : str;
  return isValid(d) ? format(d, 'd MMM') : '—';
}

const PRIORITY_COLOR = { low: '#94A3B8', medium: '#0891B2', high: '#DC2626' };
const MAX_TITLE_HTML_LENGTH = 4000;

function RichTextToolbar({ onCommand }) {
  return (
    <div className="orin-rte-toolbar" onMouseDown={(e) => e.preventDefault()}>
      <button type="button" title="Bold"      onMouseDown={() => onCommand('bold')}>          <b>B</b>  </button>
      <button type="button" title="Italic"    onMouseDown={() => onCommand('italic')}>        <i>I</i>  </button>
      <button type="button" title="Underline" onMouseDown={() => onCommand('underline')}>     <u>U</u>  </button>
      <button type="button" title="Bullets"   onMouseDown={() => onCommand('insertUnorderedList')}> • </button>
    </div>
  );
}

/* ── Cell renderers ───────────────────────────────────────── */

function CellOwner({ task }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap">
      <div className="font-semibold text-[13px] text-ink leading-tight">
        {task.owner?.full_name || '—'}
      </div>
      {task.owner?.department && (
        <div className="text-[11px] text-muted mt-0.5">{task.owner.department}</div>
      )}
    </td>
  );
}

function CellEvent({ task }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap">
      <span className="text-[11px] uppercase tracking-wide font-semibold text-muted">
        {task.event_name || '—'}
      </span>
    </td>
  );
}

function CellTask({ safeTitleHtml, taskMinWidth, isEditing, editorRef, execCmd, enterEditMode, commitEdit, handleKeyDown }) {
  return (
    <td
      className="orin-td orin-td--task px-3 py-2.5 border-b border-border-base align-top"
      style={{ minWidth: taskMinWidth }}
      onClick={(e) => isEditing && e.stopPropagation()}
    >
      <div className={`orin-task-cell ${isEditing ? 'orin-task-cell--editing' : ''}`}>
        {isEditing && <RichTextToolbar onCommand={execCmd} />}
        {isEditing ? (
          <div
            ref={editorRef}
            className="orin-task-editor"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Task title"
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div
            className="orin-task-editor"
            dangerouslySetInnerHTML={{
              __html: safeTitleHtml || '<span style="color:#94A3B8;font-weight:400">Untitled task</span>',
            }}
            onDoubleClick={enterEditMode}
          />
        )}
      </div>
    </td>
  );
}

function CellStatus({ task }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap">
      <StatusBadge status={task.status} />
    </td>
  );
}

function CellPriority({ task }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: PRIORITY_COLOR[task.priority] || '#6B7280' }}
          aria-hidden="true"
        />
        <span className="text-[12px] text-muted">
          {PRIORITY_LABELS[task.priority] || task.priority || '—'}
        </span>
      </span>
    </td>
  );
}

function CellClosure({ task }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap text-[12px] text-muted font-mono">
      {fmtDate(task.closure_date)}
    </td>
  );
}

function CellTime({ task }) {
  const mins = secondsToMinutes(task.total_seconds);
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap text-[12px] text-muted font-mono">
      {formatMinutes(mins)}<span className="text-slate-300 mx-0.5">/</span>{task.std_minutes ?? '—'}m
    </td>
  );
}

function CellRemarks({ task }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap">
      <span className="text-[12px] text-muted italic" title={task.remarks || ''}>
        {task.remarks || <span className="not-italic text-slate-300">—</span>}
      </span>
    </td>
  );
}

function CellComments({ latestComment }) {
  return (
    <td className="orin-td px-3 py-2.5 border-b border-border-base align-top whitespace-nowrap">
      {latestComment ? (
        <span className="inline-flex items-center gap-1 text-[12px] text-muted" title={latestComment}>
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="flex-shrink-0">
            <path d="M14 2H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3l3 3 3-3h3a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z"
              stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
          </svg>
          {latestComment}
        </span>
      ) : (
        <span className="text-slate-300 text-[12px]">—</span>
      )}
    </td>
  );
}

/** TL+ row actions: add/edit remark, delete. Clicks must not open the task. */
function CellActions({ task, onRemark, onDelete }) {
  return (
    <td
      className="orin-td px-2 py-2 border-b border-border-base align-top whitespace-nowrap"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="inline-flex items-center gap-1">
        {onRemark && (
          <button
            type="button"
            onClick={() => onRemark(task)}
            className="orin-row-action"
            title={task.remarks ? 'Edit remark' : 'Add remark'}
            aria-label={`${task.remarks ? 'Edit' : 'Add'} remark on task`}
          >
            <Icon name="remark" size={14} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(task)}
            className="orin-row-action orin-row-action--danger"
            title="Delete task"
            aria-label="Delete task"
          >
            <Icon name="trash" size={14} />
          </button>
        )}
      </span>
    </td>
  );
}

/* ── Main component ───────────────────────────────────────── */
function TaskRow({ task, columns, taskMinWidth = 300, latestComment, onRemark, onDelete }) {
  const navigate    = useNavigate();
  const { profile } = useAuth();
  const isOverdue   = task.status === 'overdue';

  const [isEditing, setIsEditing] = useState(false);
  const editorRef                 = useRef(null);

  // Single sanitisation pass per title value — used for both display and
  // as the seed when entering edit mode.
  const safeTitleHtml = useMemo(() => sanitizeHtml(task.title), [task.title]);
  const plainTitle    = useMemo(() => htmlToText(task.title), [task.title]);

  const savedHtmlRef = useRef(safeTitleHtml);

  useEffect(() => {
    if (!isEditing) {
      savedHtmlRef.current = safeTitleHtml;
      if (editorRef.current) editorRef.current.innerHTML = safeTitleHtml;
    }
  }, [safeTitleHtml, isEditing]);

  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.innerHTML = savedHtmlRef.current;
      editorRef.current.focus();
      const range = document.createRange();
      const sel   = window.getSelection();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isEditing]);

  const execCmd = (cmd) => document.execCommand(cmd, false, null);

  const enterEditMode = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
  }, []);

  const commitEdit = useCallback(async () => {
    const rawHtml  = editorRef.current?.innerHTML ?? savedHtmlRef.current;
    const newHtml  = sanitizeHtml(rawHtml).slice(0, MAX_TITLE_HTML_LENGTH);
    const prevHtml = savedHtmlRef.current;
    savedHtmlRef.current = newHtml;
    setIsEditing(false);

    if (!task?.id || newHtml === prevHtml) return;

    const { error: updateError } = await supabase
      .from('tasks')
      .update({ title: newHtml })
      .eq('id', task.id);

    if (updateError) {
      console.error('[ORIN] Failed to save task title:', updateError.message);
      savedHtmlRef.current = prevHtml;
      if (editorRef.current) editorRef.current.innerHTML = prevHtml;
    }
  }, [task?.id]);

  const cancelEdit = useCallback(() => {
    if (editorRef.current) editorRef.current.innerHTML = savedHtmlRef.current;
    setIsEditing(false);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape')                    { e.preventDefault(); cancelEdit();  }
    else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
  }, [commitEdit, cancelEdit]);

  const handleRowClick = useCallback(() => {
    if (isEditing) return;
    navigate(getTaskRoute(profile?.role, task.id));
  }, [isEditing, navigate, profile?.role, task.id]);

  const handleRowKeyDown = useCallback((e) => {
    if (isEditing) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(); }
  }, [isEditing, handleRowClick]);

  const rowCls = [
    'orin-tr group cursor-pointer',
    'hover:bg-[var(--row-hover)] focus:bg-[var(--row-hover)] focus:outline-none',
    isOverdue ? 'bg-red-50/40' : '',
  ].filter(Boolean).join(' ');

  const overdueStyle = isOverdue ? { boxShadow: 'inset 3px 0 0 var(--status-overdue)' } : undefined;

  const taskCellProps = { safeTitleHtml, taskMinWidth, isEditing, editorRef, execCmd, enterEditMode, commitEdit, handleKeyDown };

  return (
    <tr
      className={rowCls}
      style={overdueStyle}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Open task: ${plainTitle || 'Untitled task'}`}
    >
      {columns.map((col) => {
        switch (col.id) {
          case 'owner':    return <CellOwner    key="owner"    task={task} />;
          case 'event':    return <CellEvent    key="event"    task={task} />;
          case 'task':     return <CellTask     key="task"     {...taskCellProps} />;
          case 'status':   return <CellStatus   key="status"   task={task} />;
          case 'priority': return <CellPriority key="priority" task={task} />;
          case 'closure':  return <CellClosure  key="closure"  task={task} />;
          case 'time':     return <CellTime     key="time"     task={task} />;
          case 'remarks':  return <CellRemarks  key="remarks"  task={task} />;
          case 'comments': return <CellComments key="comments" latestComment={latestComment} />;
          default:         return null;
        }
      })}
      {(onRemark || onDelete) && (
        <CellActions task={task} onRemark={onRemark} onDelete={onDelete} />
      )}
    </tr>
  );
}

export default memo(TaskRow);

export function TaskRowSecondary() { return null; }
